import React, { useState, useEffect, useCallback } from "react"
import styles from "./index.module.css"
import { Flex, Button, Input, Empty, Card, Spin } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { Text } from "ui/Typography"
import { DigitalWaiverEmptyImage } from "ui/Icon/DSIcon/DigitalWaiverEmptyImage"
import { getGuideDigitalWaivers } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import DocumentsAddedTable from "./DocumentsAddedTable"
import { DocumentsAddedModal } from "./modal/DocumentsAdded"
import MultiSelect from "components/common/MultiSelect"
import FilterChips from "ui/FilterChips"
import { Search } from "ui/Icon/DSIcon/Search"
const categoryOptions = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" }
]

export const DocumentsAdded = () => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [editingDocument, setEditingDocument] = useState(null)
  const [searchInput, setSearchInput] = useState("")
  const [selectedCategories, setSelectedCategories] = useState([])
  const [sortState, setSortState] = useState({ sort: "", order: "" })

  const dispatch = useDispatch()
  const { items, loading, metadata } = useSelector((state) => state.digitalWaiver)
  // Initialize search term from URL on component mount

  const fetchWaivers = useCallback(async () => {
    try {
      let isActive
      const hasActive = selectedCategories.includes("active")
      const hasInactive = selectedCategories.includes("inactive")

      // Only set isActive if exactly one status is selected
      if (hasActive && !hasInactive) {
        isActive = true
      } else if (hasInactive && !hasActive) {
        isActive = false
      }
      // If both or none are selected, isActive remains undefined and won't be sent in the query

      await dispatch(getGuideDigitalWaivers(currentPage, 10, "", searchInput, isActive, sortState.sort, sortState.order)).unwrap()
    } catch (error) {
      console.error("Failed to fetch waivers:", error)
      // Handle error (e.g., show error message to user)
    }
  }, [dispatch, currentPage, searchInput, selectedCategories, sortState])

  useEffect(() => {
    fetchWaivers()
  }, [fetchWaivers])

  const onSearchChange = (e) => {
    const value = e.target.value
    setSearchInput(value)
    setCurrentPage(1)
  }
  const onManageCategoriesClick = (document = null) => {
    setEditingDocument(document)
    setIsModalOpen(true)
  }

  const handleDocumentSaved = () => {
    fetchWaivers()
    setIsModalOpen(false)
    setEditingDocument(null)
  }

  const onCategoryChange = (values) => {
    setSelectedCategories(values)
    setCurrentPage(1)
  }
  return (
    <div>
      <Flex justify="space-between" wrap="wrap" gap={10} className="my-xl">
        <Flex gap={16} align="center">
          <Input
            id="search"
            type="text"
            placeholder={t("digital_waiver_and_contracts.digital_waiver_placeholder") || "Search equipment..."}
            value={searchInput}
            onChange={onSearchChange}
            className={styles.searchInput}
            prefix={<SearchOutlined aria-hidden="true" />}
            allowClear
            aria-label={t("clients.search_clients") || "Search"}
          />
          <MultiSelect
            value={selectedCategories}
            onChange={onCategoryChange}
            options={categoryOptions}
            placeholder={selectedCategories.length > 0 ? "Statuses" : t("All Status")}
            dropdownClassName={styles.dropdownButton}
          />
        </Flex>
        <Flex gap={16} align="center" className={`ml-auto ${styles.filterActions}`}>
          <Button
            color="primary"
            variant="solid"
            size="large"
            aria-label={t("digital_waiver_and_contracts.add_waiver_and_contract") || "Add Waiver & Contract"}
            className="flex-1"
            onClick={onManageCategoriesClick}
          >
            {t("digital_waiver_and_contracts.add_waiver_and_contract")}
          </Button>
        </Flex>
      </Flex>
      <div className="mb-m">
        <FilterChips
          selectedCategories={selectedCategories}
          categoryOptions={categoryOptions}
          setSelectedCategories={setSelectedCategories}
        />
      </div>
      {/* <FirstLoading loading={loading}> */}
      <Spin spinning={loading} style={{ width: "100%" }}>
        <WithEmptyStateWrapper
          fallback={
            loading ? (
              <Spin />
            ) : (
              <Card className={`w-full ${styles.cardWrapper}`}>
                <Empty
                  image={searchInput ? <Search /> : <DigitalWaiverEmptyImage />}
                  description={
                    <Flex vertical gap={12} align="center" direction="column">
                      <Text size="lg">
                        {searchInput
                          ? "No exact matches."
                          : t("digital_waiver_and_contracts.no_digital_waiver") || "No Digital Waiver at the moment"}
                      </Text>
                      {!searchInput && (
                        <Button color="primary" variant="solid" onClick={onManageCategoriesClick}>
                          {t("digital_waiver_and_contracts.add_digital_waiver") || "Add Digital Waiver"}
                        </Button>
                      )}
                    </Flex>
                  }
                />
              </Card>
            )
          }
          hasItems={items && items.length > 0}
          name="digitalWaiver"
          tag="div"
          className="vstack gap-20"
        >
          <DocumentsAddedTable
            data={items}
            metadata={{
              ...metadata,
              page: currentPage,
              total_pages: Math.ceil((metadata?.total_results || 0) / 10)
            }}
            currentPage={currentPage}
            setCurrentPage={(page) => {
              setCurrentPage(page)
            }}
            onEdit={onManageCategoriesClick}
            sortState={sortState}
            setSortState={setSortState}
          />
        </WithEmptyStateWrapper>
      </Spin>
      {/* </FirstLoading> */}
      <DocumentsAddedModal
        visible={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingDocument(null)
        }}
        initialData={editingDocument}
        onSave={handleDocumentSaved}
      />
    </div>
  )
}
